<template>
  <div class="store-selector">
    <div class="store-selector__bar">
      <div class="store-selector__nav-shell">
        <hs-button
          @click="navigateToPreviousStore"
          :disabled="!canNavigatePrevious || isLoadingStores"
          class="store-selector__nav-btn"
          variant="secondary"
          icon="chevron-left"
          icon-only
          :title="
            previousStoreName
              ? `Previous: ${previousStoreName}`
              : 'Previous store'
          "
        />

        <div class="store-selector__select-wrap">
          <hs-select
            v-if="stores.length > 0"
            v-model="selectedStore"
            :options="getOptionsForStore()"
            placeholder="Select store"
            searchable
            search-placeholder="Search stores by name or ID..."
            empty-message="No stores found"
            icon="shopping-bag"
            :disabled="isLoadingStores"
            class="store-selector__select"
            @change="handleSelectedStore"
          />
          <div
            v-else-if="!isLoadingStores"
            class="store-selector__empty"
          >
            No stores available
          </div>
          <div
            v-else
            class="store-selector__empty"
          >
            Loading stores...
          </div>
        </div>

        <hs-button
          @click="navigateToNextStore"
          :disabled="!canNavigateNext || isLoadingStores"
          class="store-selector__nav-btn"
          variant="secondary"
          icon="chevron-right"
          icon-only
          :title="nextStoreName ? `Next: ${nextStoreName}` : 'Next store'"
        />
      </div>

      <hs-badge
        v-if="stores.length > 1"
        class="store-selector__count"
        tone="primary"
      >
        <span>{{ currentStoreIndex + 1 }}</span>
        <span>/</span>
        <span>{{ stores.length }}</span>
      </hs-badge>

    </div>
  </div>
</template>

<script>
import { HsBadge, HsButton, HsSelect } from "@/components/ui";

export default {
  name: "StoreSelector",
  components: {
    HsBadge,
    HsButton,
    HsSelect,
  },
  data() {
    return {
      selectedStore: null,
      stores: [],
      isLoadingStores: false,
    };
  },
  computed: {
    currentStoreIndex() {
      if (!this.selectedStore || this.stores.length === 0) return -1;
      return this.stores.findIndex(
        (store) => String(store.id) === String(this.selectedStore)
      );
    },
    canNavigatePrevious() {
      return this.currentStoreIndex > 0;
    },
    canNavigateNext() {
      return (
        this.currentStoreIndex >= 0 &&
        this.currentStoreIndex < this.stores.length - 1
      );
    },
    previousStoreName() {
      if (!this.canNavigatePrevious) return null;
      const prevStore = this.stores[this.currentStoreIndex - 1];
      return prevStore ? prevStore.name : null;
    },
    nextStoreName() {
      if (!this.canNavigateNext) return null;
      const nextStore = this.stores[this.currentStoreIndex + 1];
      return nextStore ? nextStore.name : null;
    },
  },
  methods: {
    getOptionsForStore() {
      return this.stores.map((store) => ({
        label: store.isOwner ? `${store.name} (You Own)` : store.name,
        value: store.id,
      }));
    },
    getSelectedStoreInfo() {
      return this.stores.find(
        (store) => String(store.id) === String(this.selectedStore)
      );
    },
    handleSelectedStore(value) {
      this.selectedStore = value;
      this.$store.commit("SET_SELECTED_STORE", value);
      this.$emit("value-change", value);
    },
    navigateToPreviousStore() {
      if (!this.canNavigatePrevious) return;

      const previousStore = this.stores[this.currentStoreIndex - 1];
      if (previousStore) {
        this.handleSelectedStore(previousStore.id);
        console.log(
          "StoreSelector: Navigated to previous store:",
          previousStore.name
        );
      }
    },
    navigateToNextStore() {
      if (!this.canNavigateNext) return;

      const nextStore = this.stores[this.currentStoreIndex + 1];
      if (nextStore) {
        this.handleSelectedStore(nextStore.id);
        console.log("StoreSelector: Navigated to next store:", nextStore.name);
      }
    },
    resolveInitialStoreId(stores = []) {
      if (!stores.length) {
        return null;
      }

      const routeStoreId = this.$route?.params?.storeId;
      if (routeStoreId) {
        const routeMatch = stores.find(
          (store) => String(store.id) === String(routeStoreId)
        );

        if (routeMatch) {
          return routeMatch.id;
        }
      }

      const persistedSelection = this.$store.state.dashboard?.selectedStore;

      if (persistedSelection) {
        const match = stores.find(
          (store) => String(store.id) === String(persistedSelection)
        );

        if (match) {
          return match.id;
        }
      }

      return stores[0]?.id ?? null;
    },
    async loadStores() {
      console.log("StoreSelector: Starting to load stores...");
      this.isLoadingStores = true;
      try {
        const { stores: fetchedStores = [] } = await this.$store.dispatch(
          "getAllUsersStore"
        );
        console.log("StoreSelector: Stores fetched:", fetchedStores);
        this.stores = fetchedStores;

        if (this.stores.length > 0) {
          localStorage.setItem("stores", JSON.stringify(this.stores));
          this.selectedStore = this.resolveInitialStoreId(this.stores);
          if (this.selectedStore) {
            this.$store.commit("SET_SELECTED_STORE", this.selectedStore);
          }
        }
        console.log("StoreSelector: Loading completed successfully");
      } catch (error) {
        console.error("StoreSelector: Failed to load stores:", error);
        this.$toast?.error?.("Failed to load stores. Please refresh the page.");
      } finally {
        console.log("StoreSelector: Setting isLoadingStores to false");
        this.isLoadingStores = false;
      }
    },
    addKeyboardNavigation() {
      this.keyboardHandler = (event) => {
        // Only handle keyboard events when the store selector is focused or when no input is focused
        const activeElement = document.activeElement;
        const isInputFocused =
          activeElement &&
          (activeElement.tagName === "INPUT" ||
            activeElement.tagName === "TEXTAREA" ||
            activeElement.contentEditable === "true");

        if (isInputFocused) return;

        // Handle arrow key navigation
        if (event.key === "ArrowLeft" && this.canNavigatePrevious) {
          event.preventDefault();
          this.navigateToPreviousStore();
        } else if (event.key === "ArrowRight" && this.canNavigateNext) {
          event.preventDefault();
          this.navigateToNextStore();
        }
      };

      document.addEventListener("keydown", this.keyboardHandler);
    },
    removeKeyboardNavigation() {
      if (this.keyboardHandler) {
        document.removeEventListener("keydown", this.keyboardHandler);
        this.keyboardHandler = null;
      }
    },
  },
  watch: {
    "$route.params.storeId"(storeId) {
      if (
        storeId &&
        this.stores.some((store) => String(store.id) === String(storeId)) &&
        String(this.selectedStore) !== String(storeId)
      ) {
        this.selectedStore = storeId;
        this.$store.commit("SET_SELECTED_STORE", storeId);
      }
    },
  },
  mounted() {
    // check if the stores are already in the local storage
    const stores = this.$safeJSONParse(localStorage.getItem("stores")) || [];
    if (stores.length > 0) {
      this.stores = stores;
      this.selectedStore = this.resolveInitialStoreId(this.stores);
      if (this.selectedStore) {
        this.$store.commit("SET_SELECTED_STORE", this.selectedStore);
      }
      // Ensure loading state is false when using cached data
      this.isLoadingStores = false;
    } else {
      // Load stores from API if not in localStorage
      this.loadStores();
    }

    // Add keyboard navigation support
    this.addKeyboardNavigation();
    window.addEventListener("hovastore:stores-updated", this.loadStores);
  },
  beforeUnmount() {
    // Remove keyboard event listeners
    this.removeKeyboardNavigation();
    window.removeEventListener("hovastore:stores-updated", this.loadStores);
  },
};
</script>

<style scoped>
.store-selector,
.store-selector__bar {
  min-width: 0;
  width: 100%;
}

.store-selector__bar {
  align-items: center;
  display: flex;
  gap: 0.6rem;
  width: 100%;
}

.store-selector__nav-shell {
  align-items: center;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.78), rgba(255, 255, 255, 0.48)),
    var(--hs-surface, #ffffff);
  border: 1px solid color-mix(in srgb, var(--hs-border, #dbe4f0) 86%, transparent);
  border-radius: 18px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
  display: flex;
  gap: 0.35rem;
  min-width: 0;
  padding: 0.28rem;
  width: 100%;
}

.store-selector__select-wrap {
  flex: 1 1 auto;
  min-width: 210px;
}

.store-selector__select {
  min-width: 210px;
}

.store-selector__select :deep(.hs-custom-select__button) {
  min-height: 2.45rem;
  border-color: transparent;
  background: color-mix(in srgb, var(--hs-bg-soft, #f8fafc) 68%, transparent);
  box-shadow: none;
}

.store-selector__empty {
  color: var(--hs-muted, #64748b);
  font-size: 0.84rem;
  font-weight: 800;
  min-width: 190px;
  padding: 0.65rem 0.75rem;
}

.store-selector__count {
  flex: 0 0 auto;
}

@media (max-width: 1023px) {
  .store-selector__bar {
    align-items: stretch;
  }

  .store-selector__nav-shell,
  .store-selector__select-wrap,
  .store-selector__select {
    min-width: 0;
    width: 100%;
  }

}

@media (max-width: 520px) {
  .store-selector__bar {
    gap: 0.4rem;
  }

  .store-selector__nav-shell {
    border-radius: 15px;
    gap: 0.25rem;
    padding: 0.22rem;
  }

  .store-selector__select :deep(.hs-custom-select) {
    min-width: 0;
  }

  .store-selector__count {
    display: none;
  }
}
</style>
